import { formatDate, formatPrice } from '@ynap/intl-formatters';
import { addPriceObjs } from '@ynap/price-utils';
import getShippingPrice from './getShippingPrice';

const CUT_OFF = 'cut_off';
const ESTIMATED_DELIVERY = 'estimated_delivery';
const TITLE = 'title';

export function getTaxByCategory(totalTaxByTaxCategory, category) {
    return totalTaxByTaxCategory.find(({ taxCategoryCode }) => taxCategoryCode === category);
}

export function getShippingOptions(state, props) {
    const { cart = {}, locale } = state;
    const { orderItem, x_totalShippingChargeWithAdjustment, totalTaxByTaxCategory = [], x_taxType } = cart;
    if (!orderItem) return [];

    // TODO: remove hasJITItems logic once BE properly deals with Same Day Delivery messages
    const hasJITItems = orderItem.some((item) => item.xitem_local_stock === 'false');

    const isDDPIncluded = x_taxType === 'DDP_INCLUDED';
    const groupedOrderItems = splitOrderItem(orderItem);

    return groupedOrderItems.map((items, index) => {
        const { requestedShipDate } = getCurrentOrderShippingInfo(state)?.[index] || {};

        if (items) {
            const { messages } = props;
            const isRemote = items?.[0]?.xitem_local_stock === 'false';
            // Get shipping options from order items
            const shippingOptions = items?.reduce((accumulator, item) => {
                const { usableShippingMode = [], usableShippingModeRemote = [] } = item;
                const usableShippingOptions = isRemote && !!usableShippingModeRemote?.length ? usableShippingModeRemote : usableShippingMode;
                usableShippingOptions.forEach((shippingOption) => {
                    const {
                        shipModeId,
                        shipModeCode,
                        description,
                        xiush_available,
                        xiush_name,
                        xiush_charge,
                        xiush_adjustedCharge,
                        xiush_currency,
                        xiush_sign_for_delivery_options = ['N'],
                        xiush_delivery_days_available = [],
                        xiush_terms_of_service = [],
                    } = shippingOption;

                    const firstSlot = xiush_delivery_days_available.length > 0 && xiush_delivery_days_available[0].delivery_date;

                    let shippingPrice = formatPrice(xiush_charge.amount / xiush_charge.divisor, xiush_currency.label, locale.id);
                    if (item.shipModeCode === shippingOption.shipModeCode && xiush_charge.amount > 0) {
                        let shippingWithPromotionAndTax = x_totalShippingChargeWithAdjustment;
                        let shippingTax = getTaxByCategory(totalTaxByTaxCategory, 'SHIPPING_VAT');
                        if (isDDPIncluded && shippingTax && (shippingTax.value?.amount || 0) > 0) {
                            shippingWithPromotionAndTax = addPriceObjs(shippingWithPromotionAndTax, shippingTax.value);
                        }
                        shippingPrice = getShippingPrice(xiush_charge, shippingWithPromotionAndTax, xiush_currency, messages, locale);
                    } else {
                        shippingPrice = getShippingPrice(xiush_charge, xiush_adjustedCharge || xiush_charge, xiush_currency, messages, locale);
                    }

                    // TODO: remove hideCutOffMessage logic once BE properly deals with Same Day Delivery messages
                    const hideCutOffMessage =
                        hasJITItems && items?.[0]?.country === 'GB' && (shipModeCode === 'PREMIER_DAYTIME' || shipModeCode === 'PREMIER_EVENING');

                    const shippingDescription = xiush_terms_of_service.reduce((desc, { code, value }) => {
                        if ((code === CUT_OFF && value !== '' && !hideCutOffMessage) || code === ESTIMATED_DELIVERY || code === TITLE) {
                            desc[code] = value;
                        }
                        return desc;
                    }, {});

                    // TODO: remove by aligning all brands on source of truth for shipping option translations
                    const descriptionArr = [];
                    if (shippingDescription[ESTIMATED_DELIVERY]) {
                        // NAP/MRP
                        if (shippingDescription[CUT_OFF] && !hideCutOffMessage) {
                            descriptionArr.push(shippingDescription[CUT_OFF]);
                        }
                        descriptionArr.push(shippingDescription[ESTIMATED_DELIVERY]);
                    } else if (typeof description !== 'undefined') {
                        // TON
                        descriptionArr.push(...description.split('\\n').map((line) => line.trim()));
                    }

                    // send analytics a non-translated name with underscores removed
                    const analyticsName = shipModeCode?.toLowerCase().replace(/_/g, ' ');

                    // Casting to string to keep original sort order
                    accumulator[`#${shipModeId}`] = {
                        fulfillmentCenterName: item.fulfillmentCenterName,
                        id: shipModeId,
                        name: shippingDescription[TITLE] || xiush_name,
                        nameEN: analyticsName,
                        shipModeCode: shipModeCode,
                        available: xiush_available === true || xiush_available === 'true',
                        description: descriptionArr,
                        finalShippingPrice: shippingPrice?.finalShippingPrice ? shippingPrice?.finalShippingPrice : shippingPrice,
                        originalShippingPrice: shippingPrice?.originalShippingPrice ? shippingPrice?.originalShippingPrice : '',
                        signature:
                            xiush_sign_for_delivery_options.length > 1
                                ? 'optional'
                                : xiush_sign_for_delivery_options.length === 1 && xiush_sign_for_delivery_options[0] === 'Y'
                                  ? 'mandatory'
                                  : 'none',
                        defaultSignatureRequired: xiush_sign_for_delivery_options[0],
                        selected: shipModeId === items?.[0]?.shipModeId,
                        defaultNominatedSlot: requestedShipDate && requestedShipDate >= firstSlot ? requestedShipDate : firstSlot || '',
                        getFormattedSlot: firstSlot
                            ? (messages) => xiush_delivery_days_available.map((slot) => formatSlot(slot.delivery_date, locale.id, messages))
                            : null
                    };
                });
                return accumulator;
            }, {});
            return values(shippingOptions);
        }
    });
}

export function getCurrentShippingOption(state, props) {
    const groupedShippingOptions = getShippingOptions(state, props);

    return groupedShippingOptions.map((shippingOptions) => {
        if (!shippingOptions) return [];
        return shippingOptions.find((option) => option.selected);
    });
}

export function getCurrentOrderShippingInfo(state) {
    const { cart = {}, locale } = state;
    const { orderItem } = cart;
    const [nonJitItems, jitItems] = splitOrderItem(orderItem);
    if (!nonJitItems?.length && !jitItems?.length) return [];

    return [nonJitItems?.[0], jitItems?.[0]].map(({ xishmd_signatureRequired: currentSignatureRequired, requestedShipDate } = {}) => ({
        currentSignatureRequired,
        requestedShipDate: requestedShipDate && requestedShipDate.split('T')[0],
        getRequestedSlot: requestedShipDate ? (messages) => formatSlot(requestedShipDate, locale.id, messages) : null,
    }));
}

export function splitOrderItem(orderItem) {
    const [nonJitItmes, jitItmes] =
        orderItem?.reduce(
            (output, item) => {
                item?.xitem_local_stock === 'false' ? output[1].push(item) : output[0].push(item);
                return output;
            },
            [[], []],
        ) || [];

    // If there are only jit items, jit shipping options should behave like non jit
    return !nonJitItmes?.length ? [jitItmes, nonJitItmes] : [nonJitItmes, jitItmes];
}

// Object.values polyfill
function values(obj) {
    return Object.keys(obj).map((key) => obj[key]);
}

export const formatSlot = (date, locale, messages) => {
    const formattedSlot = `${getRelativeDay(date, messages)}${formatDate(
        new Date(date),
        { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' },
        locale,
    )}`;
    return {
        key: date,
        value: date,
        label: formattedSlot,
        labelNative: formattedSlot,
    };
};

export function asUTC(date) {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// get relative day comparing to today or target date
export function getRelativeDay(dateStr, messages, targetDate) {
    if (!messages) return '';
    const todayTime = targetDate ? new Date(targetDate) : new Date();
    const today = asUTC(todayTime);
    const startDate = asUTC(new Date(dateStr));
    //ensure we compare time in ms in the same UTC format
    const timeDiff = startDate.getTime() - today.getTime();
    const diffDays = Math.floor(timeDiff / (1000 * 3600 * 24));
    switch (diffDays) {
        case 0:
            return messages.today();
        case 1:
            return messages.tomorrow();
        default:
            return '';
    }
}
